import { DeviceType } from "@/hooks/use-device-type";
import {
  ApiError,
  AutocompleteSuggestion,
  DAFAULT_FILTERS_FOR_SEARCH,
  DEFAULT_SEARCH,
  ListingCardData,
  ListingData,
  Search,
  SearchFacilities,
  SortingEnum,
} from "@/types/types";
import { toast } from "react-toastify";
import { create } from "zustand";
import {
  BoundingBox,
  FetchListingsResponse,
  FetchMapPointsResponse,
  ListingCardsState,
  MapClusterType,
  MapPin,
  MapState,
  SearchFiltersStatus,
} from "./types";
import { PurposeEnum, TypeEnum } from "@/types/types";
import { ChangeEvent } from "react";
import { m2ToSqft, sqftToM2 } from "@/lib/sqftToM2";
import { safeMap } from "@/lib/safemap";
import { env } from "@/lib/env";
import {
  buildMapParams,
  buildSearchParams,
} from "@/lib/buildSearchParams/buildSearchParams";
import { countActiveFilters } from "@/lib/countActiveFilters/countActiveFilters";
import {
  facilityRules,
  getAvailableFacilities,
} from "@/lib/getFacilities/getAvailableFacilities";
import { listingsNumberFormatter } from "@/lib/price-formatter";
import { getFilteredListingCountFromAutocomplete } from "@/lib/getFilteredLocationsData/autocomplete_data";
import { useListingsCacheStore } from "@/global-cache/useListingsCacheStore";
import { sleep } from "@/lib/sleep";

const LISTINGSENDPOINTURL = env.NEXT_LISTINGS_API_URL;
const SEARCHENDPOINTURL = env.NEXT_SEARCH_API_URL;

interface PageStore {
  search: Search;
  currencyRate: number;
  isImperial: boolean;
  searchFiltersStatus: SearchFiltersStatus;
  mapState: MapState;
  locationName: string;
  searchTrigger: number;
  filtersTrigger: number;
  isLoaded: boolean;
  isSearchHydrated: boolean;
  previewListingState: {
    listing: ListingCardData | null;
    isVideoPreviewActive: boolean;
    isWebTourActive: boolean;
    isMapLocationActive: boolean;
    isPhotoGalleryActive: boolean;
    isFloorPlansActive: boolean;
    isBrochureActive: boolean;
  };
  isFreeMode: boolean;
  showSearchAreaButton: boolean;
  originalLocationBounds: BoundingBox | null;
  currentLocationInvertedGeoJson: any | null;
  isFullMapView: boolean;
  savedCameraState: {
    center: [number, number];
    zoom: number;
    pitch: number;
    bearing: number;
  } | null;
  setSavedCameraState: (
    state: {
      center: [number, number];
      zoom: number;
      pitch: number;
      bearing: number;
    } | null,
  ) => void;
  cameraRestoreTrigger: number;
  triggerCameraRestore: () => void;
  setIsFullMapView: (val: boolean) => void;
  activeMapPin: MapPin | null;
  activeMapListing: ListingCardData | null;
  isActiveMapListingLoading: boolean;
  setActiveMapPin: (pin: MapPin | null, locale: string) => Promise<void>;
  enterFreeMode: (
    locale: string,
    setCache: (listings: ListingCardData[]) => void,
  ) => Promise<void>;
  setIsFreeMode: (val: boolean) => void;
  setShowSearchAreaButton: (val: boolean) => void;
  fetchMapPointsInternal: (
    tempSearch: Search,
    setCache: (listings: ListingCardData[]) => void,
    locale: string,
  ) => Promise<void>;
  applyDrawGeoJson: (
    geoJsonStr: string,
    locale: string,
    setCache: (listings: ListingCardData[]) => void,
  ) => Promise<void>;
  clearDrawGeoJson: (
    locale: string,
    setCache: (listings: ListingCardData[]) => void,
  ) => Promise<void>;
  setPreviewListingState: (
    listing: ListingCardData | null,
    isVideoPreviewActive: boolean,
    isWebTourActive: boolean,
    isMapLocationActive: boolean,
    isPhotoGalleryActive: boolean,
    isFloorPlansActive: boolean,
    isBrochureActive: boolean,
  ) => void;
  updateSearch: <K extends keyof Search>(key: K, value: Search[K]) => void;
  setType: (type: TypeEnum) => void;
  setPurpose: (purpose: PurposeEnum) => void;
  validateSeachFilters: () => boolean;
  resetSearchFilters: () => void;
  initSearch: (
    search: Search,
    userSub: string | null,
    currencyRate: number,
    isImperial: boolean,
    reverseCurrencyRate: number,
  ) => void;
  setSortingValue: (
    newValue: SortingEnum,
    locale: string,
    setCache: (listings: ListingCardData[]) => void,
  ) => Promise<void>;
  initMapState: (
    search: Search,
    mapPoints: MapPin[],
    mapClusters: MapClusterType[],
    setCache: (listings: ListingCardData[]) => void,
    boundaryGeojson?: any,
  ) => void;
  setBoundingBox: (
    topLeftLat: number,
    topLeftLon: number,
    bottomRightLat: number,
    bottomRightLon: number,
    centerLat: number,
    centerLon: number,
    currentZoom: number,
    setCache: (listings: ListingCardData[]) => void,
    locale: string,
    isCenterOutside?: boolean,
  ) => Promise<void>;
  setOnCloseMapCenter: (lat: number, lon: number, zoom: number) => void;
  fetchMapPoints: (
    setCache: (listings: ListingCardData[]) => void,
    locale: string,
  ) => Promise<void>;
  deviceType: DeviceType;
  setDeviceType: (newDeviceType: DeviceType) => void;
  isDialogOpen: boolean;
  isDialogLoading: boolean;
  openDialog: (
    listingId: string,
    locale: string,
    sub: string | null,
  ) => Promise<void>;
  closeDialog: () => void;
  activeListing: ListingData | null;
  fetchActiveListing: (
    listingId: string,
    locale: string,
    sub: string,
  ) => Promise<void>;
  ListingCardsState: ListingCardsState;
  initCardsListState: (
    initData: FetchListingsResponse,
    setCache: (listings: ListingCardData[]) => void,
  ) => void;
  fetchPageNumber: (
    pageNumber: number,
    locale: string,
    setCache: (listings: ListingCardData[]) => void,
  ) => Promise<void>;
  refetchCardsList: (
    locale: string,
    setCache: (listings: ListingCardData[]) => void,
    should_show_loading?: boolean,
  ) => Promise<void>;
  refetchState: (
    locale: string,
    setCache: (listings: ListingCardData[]) => void,
    shouldIncrement?: boolean,
  ) => Promise<void>;
  suggestions: AutocompleteSuggestion[];
  getAutoSuggestion: (
    text: string,
    locale: string,
    lat: string | null,
    long: string | null,
  ) => Promise<void>;
  isSuggestionsOpen: boolean;
  userSub: string | null;
  currentSuggestion: AutocompleteSuggestion | null;
  setIsSuggestionsOpen: (value: boolean) => void;
  text: string;
  onTextChange: (
    e: ChangeEvent<HTMLInputElement>,
    locale: string,
    lat: string | null,
    long: string | null,
  ) => void;
  setInitialSuggestion: (
    suggestion: AutocompleteSuggestion,
    locale: string,
  ) => void;
  setNewSuggestion: (
    suggestion: AutocompleteSuggestion,
    locale: string,
  ) => void;
  searchNewLocation: (
    locale: string,
    setCache: (listings: ListingCardData[]) => void,
  ) => Promise<void>;
}

let debounceTimeout: ReturnType<typeof setTimeout>;
let refetchDebounce: ReturnType<typeof setTimeout> | null = null;

export const usePageStore = create<PageStore>((set, get) => ({
  search: DEFAULT_SEARCH,
  isLoaded: false,
  searchFiltersStatus: {
    hasPriceError: false,
    hasInteriorSpaceError: false,
    hasLotSizeError: false,
    hasYearBuiltError: false,
    numberOfActiveFilters: 0,
  },
  isSearchHydrated: false,
  userSub: null,
  searchTrigger: 0,
  filtersTrigger: 0,
  mapState: {
    lat: 0,
    long: 0,
    zoom: 13,
    currentZoom: 13,
    locationType: "city",
    mapPoints: [],
    mapClusters: [],
    boundingBox: {
      topLeftLat: 0,
      topLeftLon: 0,
      bottomRightLat: 0,
      bottomRightLon: 0,
    },
  },
  currencyRate: 1,
  locationName: "",
  isImperial: true,
  previewListingState: {
    listing: null,
    isVideoPreviewActive: false,
    isWebTourActive: false,
    isMapLocationActive: false,
    isPhotoGalleryActive: false,
    isFloorPlansActive: false,
    isBrochureActive: false,
  },
  isFreeMode: false,
  showSearchAreaButton: false,
  originalLocationBounds: null,
  currentLocationInvertedGeoJson: null,
  isFullMapView: false,
  setIsFullMapView: (val) => set({ isFullMapView: val }),
  activeMapPin: null,
  activeMapListing: null,
  isActiveMapListingLoading: false,
  setActiveMapPin: async (pin, locale) => {
    const isOpening = !!pin;

    // Immediately set the pin so the map can move/open the popup
    set({
      activeMapPin: pin,
      activeMapListing: null, // Clear the old data
      isActiveMapListingLoading: isOpening,
      showSearchAreaButton: isOpening ? false : get().showSearchAreaButton,
    });

    if (pin) {
      try {
        const userSub = get().userSub;
        const params = new URLSearchParams({
          listing_id: pin.listing_id,
          locale: locale,
          ...(userSub && { user_sub: userSub }),
        });

        const response = await fetch(
          `${SEARCHENDPOINTURL}/getMapListingCard?${params.toString()}`,
        );
        if (response.ok) {
          const data = await response.json();
          // Verify the user hasn't clicked another pin while this was fetching
          if (get().activeMapPin?.listing_id === pin.listing_id) {
            useListingsCacheStore
              .getState()
              .setMultipleListings([data.listing_card]);
            set({
              activeMapListing: data.listing_card,
              isActiveMapListingLoading: false,
            });
          }
        } else {
          set({ isActiveMapListingLoading: false });
        }
      } catch (e) {
        console.error("Failed to fetch map listing card", e);
        set({ isActiveMapListingLoading: false });
      }
    }
  },
  savedCameraState: null,
  setSavedCameraState: (val) => set({ savedCameraState: val }),
  cameraRestoreTrigger: 0,
  triggerCameraRestore: () =>
    set((state) => ({ cameraRestoreTrigger: state.cameraRestoreTrigger + 1 })),
  setPreviewListingState: (
    listing: ListingCardData | null,
    isVideoPreviewActive: boolean,
    isWebTourActive: boolean,
    isMapLocationActive: boolean,
    isPhotoGalleryActive: boolean,
    isFloorPlansActive: boolean,
    isBrochureActive: boolean,
  ) => {
    set({
      previewListingState: {
        listing,
        isVideoPreviewActive,
        isWebTourActive,
        isMapLocationActive,
        isPhotoGalleryActive,
        isFloorPlansActive,
        isBrochureActive,
      },
    });
  },
  initSearch: (search, userSub, currencyRate, isImperial, reverseRate) => {
    set((state) => ({
      search: {
        ...search,
        price: {
          min: Math.floor(search.price.min * reverseRate),
          max: Math.floor(search.price.max * reverseRate),
        },
        interior_space: {
          min: isImperial
            ? m2ToSqft(search.interior_space.min)
            : search.interior_space.min,
          max: isImperial
            ? m2ToSqft(search.interior_space.max)
            : search.interior_space.max,
        },
        lot_size: {
          min: isImperial ? m2ToSqft(search.lot_size.min) : search.lot_size.min,
          max: isImperial ? m2ToSqft(search.lot_size.max) : search.lot_size.max,
        },
      },
      userSub: userSub,
      currencyRate: currencyRate,
      isImperial: isImperial,
      isSearchHydrated: true,
      searchFiltersStatus: {
        ...state.searchFiltersStatus,
        numberOfActiveFilters: countActiveFilters(search),
      },
    }));
  },
  validateSeachFilters: () => {
    const search = get().search;
    if (search.type === TypeEnum.LAND) {
      set((state) => ({
        search: {
          ...state.search,
          bathrooms: {
            number: 0,
            is_fixed: false,
          },
          bedrooms: {
            number: 0,
            is_fixed: false,
          },
          rooms: {
            number: 0,
            is_fixed: false,
          },
          interior_space: {
            min: 0,
            max: 0,
          },
        },
      }));
    }
    if (search.type === TypeEnum.APARTMENT) {
      set((state) => ({
        search: {
          ...state.search,
          lot_size: {
            min: 0,
            max: 0,
          },
        },
      }));
    }
    if (search.purpose === PurposeEnum.FORRENT) {
      set((state) => ({
        search: {
          ...state.search,
          maxBuyersFee: -1,
        },
      }));
    }
    let priceError: boolean = false;
    let interiorSpaceError: boolean = false;
    let lotSizeError: boolean = false;
    let yearBuiltError: boolean = false;
    if (search.price.min > search.price.max && search.price.max != 0) {
      priceError = true;
    }
    if (
      search.interior_space.min > search.interior_space.max &&
      search.interior_space.max != 0
    ) {
      interiorSpaceError = true;
    }
    if (search.lot_size.min > search.lot_size.max && search.lot_size.max != 0) {
      lotSizeError = true;
    }
    if (
      search.year_built.min > search.year_built.max &&
      search.year_built.max != 0
    ) {
      yearBuiltError = true;
    }
    set((state) => ({
      searchFiltersStatus: {
        ...state.searchFiltersStatus,
        hasInteriorSpaceError: interiorSpaceError,
        hasLotSizeError: lotSizeError,
        hasPriceError: priceError,
        hasYearBuiltError: yearBuiltError,
      },
    }));
    if (interiorSpaceError || lotSizeError || priceError || yearBuiltError) {
      return true;
    }
    return false;
  },
  resetSearchFilters: () => {
    set((state) => ({
      search: {
        ...state.search,
        ...DAFAULT_FILTERS_FOR_SEARCH,
      },
      searchFiltersStatus: {
        hasPriceError: false,
        hasInteriorSpaceError: false,
        hasLotSizeError: false,
        hasYearBuiltError: false,
        numberOfActiveFilters: 0,
      },
    }));
  },
  setSortingValue: async (newValue, locale, setCache) => {
    set((state) => ({
      search: {
        ...state.search,
        sorting: newValue,
        page_number: 1,
      },
    }));
    await get().refetchCardsList(locale, setCache, true);
  },
  updateSearch: (key, value) => {
    const search = get().search;
    search[key] = value;
    set((state) => ({
      search: {
        ...state.search,
        [key]: value,
      },
      searchFiltersStatus: {
        ...state.searchFiltersStatus,
        numberOfActiveFilters: countActiveFilters(search),
      },
    }));
  },
  setType: (newType) => {
    const resetFields = <Partial<Search>>{};
    const currentPurpose = get().search.purpose;
    const allowedFacilities = new Set(
      getAvailableFacilities(newType, currentPurpose),
    );

    // reset invalid facilities
    const facilitiesReset: Partial<Search> = {};
    (Object.keys(facilityRules) as (keyof SearchFacilities)[]).forEach(
      (facilityKey) => {
        if (!allowedFacilities.has(facilityKey)) {
          facilitiesReset[facilityKey] = false; // ensure disabled
        }
      },
    );

    switch (newType) {
      case TypeEnum.APARTMENT_HOUSE:
        resetFields.category = null;
        resetFields.building_type = null;
        resetFields.office_type = null;
        break;
      case TypeEnum.APARTMENT:
        resetFields.category = null;
        resetFields.building_type = null;
        resetFields.lot_size = { min: 0, max: 0 };
        resetFields.office_type = null;
        break;
      case TypeEnum.HOUSE:
        resetFields.category = null;
        resetFields.building_type = null;
        resetFields.office_type = null;
        break;
      case TypeEnum.LAND:
        resetFields.category = null;
        resetFields.building_type = null;
        resetFields.office_type = null;
        resetFields.interior_space = { min: 0, max: 0 };
        resetFields.year_built = { min: 0, max: 0 };
        resetFields.north = false;
        resetFields.south = false;
        resetFields.west = false;
        resetFields.east = false;
        resetFields.is_luxury = false;
        resetFields.new_development = false;
        break;
      case TypeEnum.OFFICE:
        resetFields.category = null;
        resetFields.building_type = null;
        resetFields.office_type = null;
        break;
    }
    set((state) => {
      const updatedSearch = {
        ...state.search,
        type: newType,
        ...resetFields,
        ...facilitiesReset,
      };
      const udpatedSearchFilterStatus = {
        ...state.searchFiltersStatus,
        numberOfActiveFilters: countActiveFilters(updatedSearch),
      };
      return {
        search: updatedSearch,
        searchFiltersStatus: udpatedSearchFilterStatus,
      };
    });
  },
  setPurpose: (newPurpose) => {
    const currentType = get().search.type;
    const allowedFacilities = new Set(
      getAvailableFacilities(currentType, newPurpose),
    );

    // reset invalid facilities
    const facilitiesReset: Partial<Search> = {};
    (Object.keys(facilityRules) as (keyof SearchFacilities)[]).forEach(
      (facilityKey) => {
        if (!allowedFacilities.has(facilityKey)) {
          facilitiesReset[facilityKey] = false; // ensure disabled
        }
      },
    );

    set((state) => {
      const updatedSearch = {
        ...state.search,
        purpose: newPurpose,
        ...facilitiesReset,
      };
      const udpatedSearchFilterStatus = {
        ...state.searchFiltersStatus,
        numberOfActiveFilters: countActiveFilters(updatedSearch),
      };
      return {
        search: updatedSearch,
        searchFiltersStatus: udpatedSearchFilterStatus,
      };
    });
  },
  deviceType: DeviceType.tablet,
  setDeviceType: (newDeviceType) => {
    set({
      deviceType: newDeviceType,
    });
  },
  isDialogOpen: false,
  isDialogLoading: false,
  openDialog: async (listingId, locale, sub) => {
    set({ isDialogOpen: true, isDialogLoading: true });
    // await new Promise((resolve) => setTimeout(resolve, 5000));
    await get().fetchActiveListing(listingId, locale, sub || "");
  },
  closeDialog: () => {
    set({ isDialogOpen: false, activeListing: null });
  },
  activeListing: null,
  fetchActiveListing: async (listingId, locale, sub) => {
    try {
      //   await new Promise((resolve) => setTimeout(resolve, 5000));
      const response = await fetch(
        `${LISTINGSENDPOINTURL}/getListing?listing_id=${listingId}&locale=${locale}&user_sub=${sub}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      if (response.ok) {
        const rawData = (await response.json()) as ListingData;
        set({ activeListing: rawData, isDialogLoading: false });
      } else {
        const apiErr = (await response.json()) as ApiError;
        throw new Error(apiErr.message);
      }
    } catch (err) {
      set({ isDialogLoading: false });
      const errorMessage =
        err instanceof Error ? err.message : "An unexpected error occurred.";
      toast(
        errorMessage === "Failed to fetch"
          ? "Unable to get listing data."
          : errorMessage,
        {
          type: "error",
        },
      );
      return;
    }
  },
  initMapState: (search, mapPoints, mapClusters, setCache, boundaryGeojson) => {
    const initialBounds = {
      topLeftLat: search.bouding_box.top_left_lat,
      topLeftLon: search.bouding_box.top_left_lon,
      bottomRightLat: search.bouding_box.bottom_right_lat,
      bottomRightLon: search.bouding_box.bottom_right_lon,
    };
    set((state) => ({
      isFreeMode: search.location_url === "map",
      originalLocationBounds: initialBounds,
      currentLocationInvertedGeoJson: boundaryGeojson ?? null,
      mapState: {
        ...state.mapState,
        boundingBox: {
          topLeftLat: search.bouding_box.top_left_lat,
          topLeftLon: search.bouding_box.top_left_lon,
          bottomRightLat: search.bouding_box.bottom_right_lat,
          bottomRightLon: search.bouding_box.bottom_right_lon,
        },
        zoom: search.bouding_box.zoom,
        currentZoom: search.bouding_box.zoom,
        mapPoints: mapPoints,
        mapClusters: mapClusters,
      },
    }));
    return;
  },
  setBoundingBox: async (
    topLeftLat,
    topLeftLon,
    bottomRightLat,
    bottomRightLon,
    centerLat,
    centerLon,
    currentZoom,
    setCache,
    locale,
    isCenterOutside = false,
  ) => {
    const state = get();
    const isFreeMode = state.isFreeMode;
    const originalBounds = state.originalLocationBounds;

    // A. Always update the map's visual "mapState" (the pins need this)
    set((state) => ({
      mapState: {
        ...state.mapState,
        currentZoom: currentZoom,
        boundingBox: { topLeftLat, topLeftLon, bottomRightLat, bottomRightLon },
      },
      search: {
        ...state.search,
        bouding_box: {
          top_left_lat: topLeftLat,
          top_left_lon: topLeftLon,
          bottom_right_lat: bottomRightLat,
          bottom_right_lon: bottomRightLon,
          zoom: currentZoom,
        },
        center: {
          lat: centerLat,
          lon: centerLon,
        },
      },
    }));

    // B. Detect Drift using Area Intersection Coverage
    if (!isFreeMode && originalBounds) {
      const iNorth = Math.min(originalBounds.topLeftLat, topLeftLat);
      const iSouth = Math.max(originalBounds.bottomRightLat, bottomRightLat);
      const iWest = Math.max(originalBounds.topLeftLon, topLeftLon);
      const iEast = Math.min(originalBounds.bottomRightLon, bottomRightLon);

      let coverage = 0;
      if (iNorth > iSouth && iEast > iWest) {
        const intersectionArea = (iNorth - iSouth) * (iEast - iWest);
        const currentArea =
          (topLeftLat - bottomRightLat) * (bottomRightLon - topLeftLon);
        coverage = currentArea > 0 ? intersectionArea / currentArea : 0;
      }

      // 🚀 CRITICAL FIX: If coverage is low OR the center of the screen is on the dark tint!
      if (coverage < 0.3 || isCenterOutside) {
        set({ showSearchAreaButton: true });
      } else {
        set({ showSearchAreaButton: false });
      }

      const tempSearch = {
        ...state.search,
        bouding_box: {
          top_left_lat: topLeftLat,
          top_left_lon: topLeftLon,
          bottom_right_lat: bottomRightLat,
          bottom_right_lon: bottomRightLon,
          zoom: currentZoom,
        },
      };
      await state.fetchMapPointsInternal(tempSearch, setCache, locale);
      return;
    }

    // C. Free Mode
    if (isFreeMode) {
      set((state) => ({
        search: {
          ...state.search,
          location_url: "map",
          location_name: "",
          bouding_box: {
            top_left_lat: topLeftLat,
            top_left_lon: topLeftLon,
            bottom_right_lat: bottomRightLat,
            bottom_right_lon: bottomRightLon,
            zoom: currentZoom,
          },
          center: { lat: centerLat, lon: centerLon },
        },
      }));
      await state.refetchState(locale, setCache, true);
    }
  },
  enterFreeMode: async (locale, setCache) => {
    const currentMapBounds = get().mapState.boundingBox;
    const currentZoom = get().mapState.currentZoom;

    set((state) => ({
      isFreeMode: true,
      showSearchAreaButton: false,
      locationName: "",
      text: "",
      currentSuggestion: null,
      currentLocationInvertedGeoJson: null,
      search: {
        ...state.search,
        location_url: "map",
        location_name: "",
        page_number: 1, // Reset pagination
        bouding_box: {
          top_left_lat: currentMapBounds.topLeftLat,
          top_left_lon: currentMapBounds.topLeftLon,
          bottom_right_lat: currentMapBounds.bottomRightLat,
          bottom_right_lon: currentMapBounds.bottomRightLon,
          zoom: currentZoom,
        },
      },
    }));

    // Trigger full refresh
    await get().refetchState(locale, setCache, true);
  },

  // Helper for background map pin updates without affecting the list
  fetchMapPointsInternal: async (tempSearch, setCache, locale) => {
    // Same logic as your fetchMapPoints but uses tempSearch instead of state.search
    const userSub = get().userSub;
    const rate = get().currencyRate;
    const isImperial = get().isImperial;
    const queryParams = buildMapParams(
      tempSearch,
      locale,
      userSub,
      isImperial,
      rate,
    );
    const queryString = new URLSearchParams(queryParams).toString();

    try {
      const response = await fetch(
        `${SEARCHENDPOINTURL}/getMapPoints?${queryString}`,
      );
      if (response.ok) {
        const data = await response.json();
        set((s) => ({
          mapState: {
            ...s.mapState,
            mapPoints: data.points_array,
            mapClusters: data.clusters,
          },
        }));
      }
    } catch (e) {
      console.error(e);
    }
  },
  setIsFreeMode: (val) => set({ isFreeMode: val }),
  setShowSearchAreaButton: (val) => set({ showSearchAreaButton: val }),
  applyDrawGeoJson: async (geoJsonStr, locale, setCache) => {
    const state = get();
    let shouldBeFreeMode = state.isFreeMode;

    // Detect if the drawing breaks outside the city bounds
    if (!shouldBeFreeMode && state.originalLocationBounds) {
      const parsed = JSON.parse(geoJsonStr);
      const coords = parsed.coordinates[0]; // The points of the polygon
      const b = state.originalLocationBounds;

      for (const pt of coords) {
        const lng = pt[0],
          lat = pt[1];
        // If any point is outside the original location box, we switch to Free Mode
        if (
          lat > b.topLeftLat ||
          lat < b.bottomRightLat ||
          lng < b.topLeftLon ||
          lng > b.bottomRightLon
        ) {
          shouldBeFreeMode = true;
          break;
        }
      }
    }

    if (shouldBeFreeMode) {
      set((state) => ({
        isFreeMode: true,
        showSearchAreaButton: false,
        locationName: "", // Clear name, we are now in custom map mode
        search: {
          ...state.search,
          location_url: "map",
          page_number: 1,
          draw_geojson: geoJsonStr,
        },
      }));
    } else {
      set((state) => ({
        search: {
          ...state.search,
          page_number: 1,
          draw_geojson: geoJsonStr,
        },
      }));
    }

    await get().refetchState(locale, setCache, true);
  },

  clearDrawGeoJson: async (locale, setCache) => {
    set((state) => ({
      search: {
        ...state.search,
        page_number: 1,
        draw_geojson: "",
      },
    }));
    await get().refetchState(locale, setCache, true);
  },
  setOnCloseMapCenter: (lat: number, lon: number, zoom: number) =>
    set((state) => ({
      mapState: {
        ...state.mapState,
        lat,
        long: lon,
        zoom,
      },
    })),
  fetchMapPoints: async (setCache, locale) => {
    const search = get().search;
    const userSub = get().userSub;
    const rate = get().currencyRate;
    const isImperial = get().isImperial;
    const queryParams = buildMapParams(
      search,
      locale,
      userSub,
      isImperial,
      rate,
    );

    const queryString = new URLSearchParams(queryParams).toString();
    try {
      const response = await fetch(
        `${SEARCHENDPOINTURL}/getMapPoints?${queryString}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      if (response.ok) {
        const data = (await response.json()) as FetchMapPointsResponse;
        set((state) => ({
          mapState: {
            ...state.mapState,
            mapPoints: data.points_array,
            mapClusters: data.clusters,
          },
        }));
      } else {
        const apiErr = (await response.json()) as ApiError;
        throw new Error(apiErr.message);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An unexpected error occurred.";
      toast(
        errorMessage === "Failed to fetch"
          ? "Unable to get more map listings."
          : errorMessage,
        {
          type: "error",
        },
      );
      return;
    }
  },
  ListingCardsState: {
    listingsIds: [],
    total_count: 0,
    page_number: 1,
    total_number_of_pages: 1,
    loadingNextPageData: false,
    breadcrumbs: [],
  },
  initCardsListState: (initData, setCache) => {
    let firstPageListings: ListingCardData[];
    let firstPageListingsIds: string[];
    firstPageListings = initData.listings;
    firstPageListingsIds = firstPageListings.map(
      (listing) => listing.listing_id,
    );
    setCache([...firstPageListings]);
    set((state) => ({
      ListingCardsState: {
        ...state.ListingCardsState,
        total_count: initData.total_count,
        page_number: initData.page_number,
        total_number_of_pages: initData.number_of_pages,
        loadingNextPageData: false,
        listingsIds: firstPageListingsIds,
        breadcrumbs: initData.breadcrumbs || [],
      },
      search: {
        ...state.search,
        listing_count: initData.total_count,
      },
      isLoaded: true,
    }));
  },
  fetchPageNumber: async (pageNumber, locale, setCache) => {
    set((state) => ({
      search: {
        ...state.search,
        page_number: pageNumber,
      },
    }));
    await get().refetchCardsList(locale, setCache, true);
  },
  refetchCardsList: async (locale, setCache, should_show_loading = false) => {
    const isAlreadyLoading = get().ListingCardsState.loadingNextPageData;
    if (isAlreadyLoading) {
      return;
    }

    // will wait 300ms before setting is loading (in case response is faster than 250ms)
    let loadingTimer: NodeJS.Timeout | null = null;

    if (should_show_loading) {
      loadingTimer = setTimeout(() => {
        set((state) => ({
          ListingCardsState: {
            ...state.ListingCardsState,
            loadingNextPageData: true,
          },
        }));
      }, 300);
    }
    const search = get().search;
    const rate = get().currencyRate;
    const isImperial = get().isImperial;
    const userSub = get().userSub;
    const queryParams = buildSearchParams(
      search,
      locale,
      userSub,
      isImperial,
      rate,
    );

    const queryString = new URLSearchParams(queryParams).toString();
    try {
      const response = await fetch(
        `${SEARCHENDPOINTURL}/getSearchResult?${queryString}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      if (response.ok) {
        const data = (await response.json()) as FetchListingsResponse;
        get().initCardsListState(data, setCache);
      } else {
        const apiErr = (await response.json()) as ApiError;
        throw new Error(apiErr.message);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An unexpected error occurred.";
      throw Error(
        errorMessage === "Failed to fetch"
          ? "Unable to get search result data."
          : errorMessage,
      );
    } finally {
      if (loadingTimer) {
        clearTimeout(loadingTimer);
      }
      if (should_show_loading) {
        set((state) => ({
          ListingCardsState: {
            ...state.ListingCardsState,
            loadingNextPageData: false,
          },
        }));
      }
    }
  },
  refetchState: async (locale, setCache, shouldIncrement = false) => {
    set((state) => ({
      search: {
        ...state.search,
        page_number: 1,
      },
      filtersTrigger: shouldIncrement
        ? (state.filtersTrigger ?? 0) + 1
        : state.filtersTrigger,
    }));
    if (refetchDebounce) {
      clearTimeout(refetchDebounce);
    }

    refetchDebounce = setTimeout(async () => {
      const isNotValid = get().validateSeachFilters();
      if (isNotValid) return;

      await Promise.all([
        get().refetchCardsList(locale, setCache),
        get().fetchMapPoints(setCache, locale),
      ]);
    }, 300);
  },
  suggestions: [],
  currentSuggestion: null,
  isSuggestionsOpen: false,
  setIsSuggestionsOpen: (value) => {
    set({ isSuggestionsOpen: value });
  },
  text: "",
  onTextChange: (e, locale, lat, long) => {
    e.preventDefault();
    const text = e.target.value;
    set({ text: text });
    get().getAutoSuggestion(text, locale, lat, long);
  },
  getAutoSuggestion: async (text, locale, lat, long) => {
    if (debounceTimeout) clearTimeout(debounceTimeout);

    if (text.length < 2) {
      set({ suggestions: [], isSuggestionsOpen: false });
      return;
    }

    debounceTimeout = setTimeout(async () => {
      try {
        const resp = await fetch(
          `${SEARCHENDPOINTURL}/getAutocomplete?text=${encodeURIComponent(
            text,
          )}&locale=${locale}`,
          {
            headers: {
              "Content-Type": "application/json",
              "x-viewer-lat": lat || "",
              "x-viewer-long": long || "",
            },
          },
        );
        if (resp.ok) {
          const data = (await resp.json()) as {
            locations_array: AutocompleteSuggestion[];
          };
          set({ suggestions: data.locations_array, isSuggestionsOpen: true });
        } else {
          const apiErr = (await resp.json()) as ApiError;
          throw new Error(apiErr.message);
        }
      } catch (err) {
        set({ suggestions: [], isSuggestionsOpen: false });
        const errorMessage =
          err instanceof Error ? err.message : "An unexpected error occurred.";
        toast(
          errorMessage === "Failed to fetch"
            ? "Unable to get location suggestions."
            : errorMessage,
          {
            type: "error",
          },
        );
        return;
      }
    }, 300);
  },
  setInitialSuggestion: (suggestion, locale) => {
    const location_name = suggestion.names[locale] ?? suggestion.names["en"];
    set((state) => ({
      search: {
        ...state.search,
        location_url:
          suggestion.search_urls[locale] ?? suggestion.search_urls["en"],
        location_name: suggestion.names[locale] ?? suggestion.names["en"],
        center: {
          lat: suggestion.geo_location.lat,
          lon: suggestion.geo_location.lon,
        },
        location_type: suggestion.type,
        bouding_box: {
          ...state.search.bouding_box,
          top_left_lat: suggestion.map_view.north,
          top_left_lon: suggestion.map_view.west,
          bottom_right_lat: suggestion.map_view.south,
          bottom_right_lon: suggestion.map_view.east,
        },
      },
      locationName: location_name,
      currentSuggestion: suggestion,
      text: suggestion.names[locale] ?? suggestion.names["en"] ?? "",
      suggestions: [],
      isSuggestionsOpen: false,
    }));
  },
  setNewSuggestion: (suggestion, locale) => {
    set({
      currentSuggestion: suggestion,
      text: suggestion.names[locale] ?? suggestion.names["en"],
      suggestions: [],
      isSuggestionsOpen: false,
    });
  },
  searchNewLocation: async (locale, setCache) => {
    const suggestion = get().currentSuggestion;
    if (suggestion === null) {
      return;
    }
    const location_name = suggestion.names[locale] ?? suggestion.names["en"];

    // We create the new bounds from the suggestion to anchor the map to the new city
    const camelBounds = {
      topLeftLat: suggestion.map_view.north,
      topLeftLon: suggestion.map_view.west,
      bottomRightLat: suggestion.map_view.south,
      bottomRightLon: suggestion.map_view.east,
    };

    const snakeBounds = {
      top_left_lat: suggestion.map_view.north,
      top_left_lon: suggestion.map_view.west,
      bottom_right_lat: suggestion.map_view.south,
      bottom_right_lon: suggestion.map_view.east,
    };

    set((state) => ({
      searchTrigger: (state.searchTrigger ?? 0) + 1,

      // ✅ RESET TO LOCATION MODE
      isFreeMode: false,
      showSearchAreaButton: false,
      originalLocationBounds: camelBounds, // Anchor drift detection to the new city
      currentLocationInvertedGeoJson:
        suggestion.inverted_boundary_geojson ?? null,
      search: {
        ...state.search,
        location_name: suggestion.names[locale] ?? suggestion.names["en"],
        listing_count: getFilteredListingCountFromAutocomplete(
          suggestion,
          state.search.type,
          state.search.purpose,
        ),
        location_url:
          suggestion.search_urls[locale] ?? suggestion.search_urls["en"],
        center: {
          lat: suggestion.geo_location.lat,
          lon: suggestion.geo_location.lon,
        },
        location_type: suggestion.type,
        page_number: 1,
        bouding_box: {
          ...state.search.bouding_box,
          ...snakeBounds, // Spread the new bounds
          zoom: state.search.bouding_box.zoom, // keep current zoom or set a default like 10
        },
      },
      locationName: location_name,
      mapState: {
        ...state.mapState,
        lat: suggestion.geo_location.lat,
        long: suggestion.geo_location.lon,
        locationType: suggestion.type,
        boundingBox: camelBounds,
      },
      ListingCardsState: {
        ...state.ListingCardsState,
        total_count: getFilteredListingCountFromAutocomplete(
          suggestion,
          state.search.type,
          state.search.purpose,
        ),
      },
    }));
    await Promise.all([
      get().refetchCardsList(locale, setCache, true),
      get().fetchMapPoints(setCache, locale),
    ]);
  },
}));
